// responseSla.mjs — 响应时限在**成员面**上的纯逻辑（SB-190 §3.1 P2／§8，第二百四十七批，步 5）。
// 无 React、无 DOM（SB-042：可判定的东西离开 JSX 才有判据）。
//
// ── 它读的是什么 ─────────────────────────────────────────────────────────────
// 后端 `route_util::response_deadline_json` 的形状——201 回执、`GET /api/me/tickets/{no}`
// 详情、站内信行三处同一份投影：
//   { channel, itemRef, markedAtMs, baseDay, sameDayRank, rule, startMs, deadlineMs,
//     deadlineAt, effectiveDeadlineMs, deadlineFrozen, state }
// 数字来自 `ResponseSlaStore::clock_for_item`，与 `report()`（管理页、`sla mine`）同一条路，
// 所以成员在自己工单页看到的截止时刻与打分方看到的**逐字节相同**（设计 §0-12 对钟点条
// 同样成立）。这里只做形状收敛与「钟点条要哪几位」，**不重算**任何一个数：前端再算一遍
// 工作日历，就是同一条判据的第二个实现，而它漂开的那天两边都"没错"。
//
// ── 为什么值域与标签键放在这里、管理页来 import ───────────────────────────────
// `sla.state.*` / `sla.rule.*` 两族标签键（第二百四十六批落在 admin.responseSla.* 下）
// 从本批起由成员面与管理面共用：一个成员页去查 admin.responseSla.rule.* 是「两处各存
// 一份」的形状，而两份标签迟早对不上。adminResponseSla.mjs 从这里 re-export，
// 它自己的 kind 族（裁决种类）仍是管理员词汇，不动。
//
// ── fail-closed 的方向 ────────────────────────────────────────────────────────
// 值域外的 rule / state（服务端先上线了新值）**原串保留**，标签键退回 null——调用侧印原码
// 而不是硬套一个已知标签：这一条是 D4 的证据面，替读面说话比印一个奇怪的码更坏。
// 不是对象、没有有限的 deadlineMs（null、老后端缺席、垃圾）⇒ null = 没有钟点条。

// 服务端值域的副本（DP-2 的既有姿态；与 response_sla_store 的 evaluate 同一组串）。
export const SLA_STATES = Object.freeze(['on_time', 'on_time_provisional', 'late', 'needs_ruling', 'pending', 'excluded']);
export const SLA_RULES = Object.freeze(['1bd', '1bd_shift', '1bd_defer']);

// 合同常量的镜像——与 backend/src/core/defaults.h 同名常量逐条对账（helpContent.test.mjs
// 真的去读那份头文件）。手册与提交页的 P2 提示都从这里取数，正文里不手写 3 与 1。
export const SLA_SAME_DAY_THRESHOLD = 3;   // kSlaSameDayThreshold：同一工作日超过 3 件即顺延
export const SLA_RESPONSE_WORKING_DAYS = 1; // kSlaResponseWorkingDays：1 个工作日内首次实质响应

// 状态芯片配色。late 用 error 而不是 warning：逾期是一句已经成立的话，不是提醒。
// pending / excluded 走 default——期限内未答不该长得像警告（受指派人正在期限内），
// 不计更不该有颜色。
export const SLA_STATE_COLOR = Object.freeze({
  on_time: 'success', on_time_provisional: 'success', late: 'error',
  needs_ruling: 'warning', pending: 'default', excluded: 'default',
});

export const stateKey = (s) => (SLA_STATES.includes(s) ? `sla.state.${s}` : null);
export const ruleKey = (r) => (SLA_RULES.includes(r) ? `sla.rule.${r}` : null);

// 时刻收敛。⚠ Number(null) 是 0（messages.mjs 的 num 同一条教训）：null / undefined /
// 空串给 null，绝不让一个缺席的时刻在界面上印成 1970-01-01。
const ms = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const int = (v) => {
  const n = ms(v);
  return n === null ? 0 : Math.trunc(n);
};
const str = (v) => (typeof v === 'string' ? v : (v === null || v === undefined ? '' : String(v)));

// 只认对象 + 有限的 deadlineMs；其余 ⇒ null（= 没有钟点条：非 ASK 的工单、未标记或
// 排着队还没发出的站内信、老后端）。幂等：喂一份已归一的对象得到同一份。
export function normalizeResponseDeadline(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const deadlineMs = ms(raw.deadlineMs);
  if (deadlineMs === null) return null;
  // 顺延后的生效期限缺席（老后端）⇒ 等于合同期限：extended 恒 false，而不是把 0 当成
  // 「提前到 1970 年」。
  const effective = ms(raw.effectiveDeadlineMs);
  return {
    channel: str(raw.channel),
    itemRef: int(raw.itemRef),
    markedAtMs: ms(raw.markedAtMs),
    baseDay: int(raw.baseDay),
    sameDayRank: int(raw.sameDayRank),
    rule: str(raw.rule),
    startMs: ms(raw.startMs),
    deadlineMs,
    deadlineAt: str(raw.deadlineAt),
    effectiveDeadlineMs: effective === null ? deadlineMs : effective,
    deadlineFrozen: raw.deadlineFrozen === true,
    state: str(raw.state),
  };
}

// ── 钟点条的内容 ────────────────────────────────────────────────────────────
// 「截止 … · 规则 1bd_defer（该日第 4 件）」+ 状态芯片（设计 §8）。返回渲染要的那几位：
//   dueMs      合同期限（deadlineMs；正文按读者本地时区印，SOP-11 帧挂在 dueAt 上）
//   dueAt      日历时区的 ISO（"…-07:00"），作 title
//   effectiveMs 顺延后的生效期限（== dueMs 时不另起一行）
//   rule / ruleKey / rank / state / stateKey / color
//   deferred   rule === '1bd_defer'（提交页 P2 提示的开关：「该日已有 N 件，本件自次一工作日起算」）
//   extended   effectiveMs > dueMs（裁决 extend 之后）
//   frozen     deadlineFrozen（裁决落下后期限不再随日历表变）
// 变异靶：deferred 按 rank 而不按 rule 判（rank 4 但假日顺延与 defer 是两回事）；extended
// 用 >=（等于不是顺延）；frozen 从 state 猜。
export function deadlineBar(raw) {
  const rd = normalizeResponseDeadline(raw);
  if (!rd) return null;
  return {
    dueMs: rd.deadlineMs,
    dueAt: rd.deadlineAt,
    effectiveMs: rd.effectiveDeadlineMs,
    rule: rd.rule,
    ruleKey: ruleKey(rd.rule),
    rank: rd.sameDayRank,
    state: rd.state,
    stateKey: stateKey(rd.state),
    color: SLA_STATE_COLOR[rd.state] ?? 'default',
    deferred: rd.rule === '1bd_defer',
    extended: rd.effectiveDeadlineMs > rd.deadlineMs,
    frozen: rd.deadlineFrozen,
  };
}
